import React, { useContext, useEffect, useState } from "react";

import "./charts/dashboard.css";

import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";
import {
  _t,
  getCookie,
  tableLoading,
  customStyle,
  branchSelectStyles,
  BranchMultiValue,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import DatePicker from "react-datepicker";
import moment from "moment";

import ReportSidebar from "./ReportSidebar";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import { UserContext } from "../../../../contexts/User";
import FinanceStatsShow from "./FinanceDashboard/FinanceStatsShow";
import PayableAgeingWidget from "./FinanceDashboard/PayableAgeingWidget";
import AssetsVsLiabilitiesWidget from "./FinanceDashboard/AssetsVsLiabilitiesWidget";
import CashInflowOutflowWidget from "./FinanceDashboard/CashInflowOutflowWidget";
import CashPositionWidget from "./FinanceDashboard/CashPositionWidget";
import "./FinanceDashboard/FinanceDashboard.css";


const FinanceDashboard = () => {
  const { t } = useTranslation();
  const { branchForSearch } = useContext(RestaurantContext);
  const { authUserInfo } = useContext(UserContext);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [branch, setBranch] = useState(null);
  const [cardsData, setCardsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
    branch: null,
  });

  const fetchCardsData = async (sDate = startDate, eDate = endDate, selectedBranch = branch) => {
    setIsLoading(true);
    const fromDate = sDate ? moment(sDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
    const toDate = eDate ? moment(eDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");

    const payload = {
      fromDate,
      toDate,
    };

    if (selectedBranch) {
      if (Array.isArray(selectedBranch) && selectedBranch.length > 0) {
        payload.branch_id = selectedBranch.map((b) => b.id);
      } else if (selectedBranch?.id) {
        payload.branch_id = selectedBranch.id;
      }
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/widgets/accounting-cards-data`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${getCookie()}`,
          },
        }
      );

      if (response?.data?.data && Array.isArray(response.data.data)) {
        setCardsData(response.data.data);
      } else if (Array.isArray(response?.data)) {
        setCardsData(response.data);
      }
    } catch (error) {
      console.error("Error fetching accounting cards data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      authUserInfo?.details?.user_type === "staff" ||
      authUserInfo?.details?.user_type === "manager"
    ) {
      const branchFilter = branchForSearch?.find(
        (item) => item.id === authUserInfo.details.branch_id
      );
      if (branchFilter) {
        setBranch([branchFilter]);
        setAppliedFilters((prev) => ({ ...prev, branch: [branchFilter] }));
      }
    }
  }, [authUserInfo, branchForSearch]);

  useEffect(() => {
    fetchCardsData(startDate, endDate, branch);
  }, []);

  const handleBranch = (selectedBranch) => {
    setBranch(selectedBranch);
  };

  const handleApplyFilter = () => {
    setAppliedFilters({
      startDate: startDate,
      endDate: endDate,
      branch: branch,
    });
    fetchCardsData(startDate, endDate, branch);
  };

  const isBranchDisabled =
    authUserInfo?.details?.user_type === "staff" ||
    authUserInfo?.details?.user_type === "manager";

  return (
    <>
      <Helmet>
        <title>{_t(t("Finance Dashboard"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15 py-2">
                      <div key="finance-dashboard">
                        {/* Header Bar */}
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 t-pt-10 pb-3 border-bottom mb-4">
                          <div className="d-flex align-items-center">
                            <h5 className="m-0 font-weight-bold text-dark tracking-tight">
                              {_t(t("Finance Dashboard"))}
                            </h5>
                          </div>

                          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-start justify-content-md-end flex-grow-1">
                            <div style={{ width: "150px" }}>
                              <DatePicker
                                selected={startDate}
                                onChange={(value) => setStartDate(value)}
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                className="form-control form-control-sm py-1 rounded shadow-none finance-date-picker"
                                placeholderText={_t(t("From date"))}
                                shouldCloseOnSelect
                              />
                            </div>

                            <div style={{ width: "150px" }}>
                              <DatePicker
                                selected={endDate}
                                onChange={(value) => setEndDate(value)}
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                className="form-control form-control-sm py-1 rounded shadow-none finance-date-picker"
                                placeholderText={_t(t("To date"))}
                                shouldCloseOnSelect
                              />
                            </div>

                            <div style={{ minWidth: "300px", maxWidth: "450px" }} className="flex-grow-1 flex-md-grow-0">
                            <Select
                              options={branchForSearch || []}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              value={branch}
                              onChange={handleBranch}
                              classNamePrefix="select"
                              className="sm-text"
                              isClearable
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              components={{
                                MultiValue: BranchMultiValue,
                                MultiValueRemove: () => null,
                              }}
                              styles={branchSelectStyles}
                              maxMenuHeight="180px"
                              placeholder={_t(t("Select a branch")) + ".."}
                              isDisabled={isBranchDisabled}
                            />
                            </div>

                            <button
                              type="button"
                              className="btn btn-primary btn-sm px-2 rounded d-inline-flex align-items-center justify-content-center gap-1 font-weight-semibold shadow-sm"
                              onClick={handleApplyFilter}
                              title={_t(t("Apply Filter"))}
                            >
                              <span>{_t(t("Apply"))}</span>
                            </button>
                          </div>
                        </div>

                        {/* Row 1: Key Financial Stats Cards */}
                        <div className="mb-4">
                          <FinanceStatsShow cardsData={cardsData} isLoading={isLoading} />
                        </div>

                        {/* Row 2: Payable Ageing & Assets vs Liabilities */}
                        <div className="row gx-3 gy-4 mb-4">
                          <div className="col-12 col-lg-6">
                            <PayableAgeingWidget
                              startDate={appliedFilters.startDate}
                              endDate={appliedFilters.endDate}
                              branch={appliedFilters.branch}
                            />
                          </div>
                          <div className="col-12 col-lg-6">
                            <AssetsVsLiabilitiesWidget
                              startDate={appliedFilters.startDate}
                              endDate={appliedFilters.endDate}
                              branch={appliedFilters.branch}
                            />
                          </div>
                        </div>

                        {/* Row 3: Cash Inflow/Outflow & Cash Position */}
                        <div className="row gx-3 gy-4 mb-4">
                          <div className="col-12 col-lg-8">
                            <CashInflowOutflowWidget
                              startDate={appliedFilters.startDate}
                              endDate={appliedFilters.endDate}
                              branch={appliedFilters.branch}
                            />
                          </div>
                          <div className="col-12 col-lg-4">
                            <CashPositionWidget
                              startDate={appliedFilters.startDate}
                              endDate={appliedFilters.endDate}
                              branch={appliedFilters.branch}
                            />
                          </div>
                        </div>
                      </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default FinanceDashboard;
